import { Editor, type Extensions } from "@tiptap/core";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useRef } from "preact/hooks";

export type TiptapEditorProps = {
  value: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  class?: string;
  extensions?: Extensions;
};

export function TiptapEditor({
  value,
  onChange,
  placeholder,
  autoFocus,
  class: className,
  extensions: extraExtensions,
}: TiptapEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<Editor | null>(null);
  const lastEmittedHtml = useRef("");
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Create editor once on mount
  useEffect(() => {
    if (!containerRef.current) return;

    const editor = new Editor({
      element: containerRef.current,
      extensions: [
        StarterKit.configure({
          heading: false,
          bulletList: false,
          orderedList: false,
          blockquote: false,
          codeBlock: false,
          code: false,
          horizontalRule: false,
          listItem: false,
        }),
        Underline,
        Link.configure({ openOnClick: false }),
        ...(placeholder ? [Placeholder.configure({ placeholder })] : []),
        ...(extraExtensions ?? []),
      ],
      content: value || "",
      autofocus: autoFocus ? "end" : false,
      editorProps: {
        attributes: {
          class: "tiptap",
        },
      },
      onUpdate: ({ editor: ed }) => {
        const html = ed.getHTML();
        lastEmittedHtml.current = html;
        onChangeRef.current?.(html);
      },
    });

    editorRef.current = editor;

    return () => {
      editor.destroy();
      editorRef.current = null;
    };
  }, []);

  // Sync external value changes
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    // Fast path: skip if the value matches what we last emitted (our own change round-tripping)
    if (value === lastEmittedHtml.current) return;

    // Slow path: normalize HTML for comparison (handles ProseMirror whitespace differences)
    const currentHtml = editor.getHTML();
    if (normalizeHtml(currentHtml) !== normalizeHtml(value)) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
  }, [value]);

  return <div ref={containerRef} class={className} />;
}

function normalizeHtml(html: string): string {
  if (!html || html === "<p></p>") return "";
  // Use a DOM element to normalize attribute order, whitespace, self-closing tags
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.innerHTML;
}
